import { SUBSECTION_TEMPLATES } from "./subsectionTemplates";
import { CATEGORY_KUNTO_ROWS } from "./categoryKuntoRows";

export const TEMPLATE_SENTINEL = '{{template_dynamic_subsection_name}}';
const DYN = '{{dynamic_subsection_name}}';

// Matches any {{...template...}} tag — covers both our sentinel and user-defined ones
// like {{subsection_template_parvekkeet}}.
export const isSentinelTitle = (title) => /\{\{[^}]*template[^}]*\}\}/.test(title ?? '');

// Matches {{section_template_<category>}} — signals "create a full section per chip" mode.
export const isSectionTemplateSentinel = (title) => /\{\{section_template_[^}]+\}\}/.test(title ?? '');
export const getSectionTemplateCategory = (title) => {
  const m = title?.match(/\{\{section_template_([^}]+)\}\}/);
  return m ? m[1] : null;
};

// Matches template-only sections that must be hidden in real reports.
// Covers both the old subsection_container pattern and the new section_template pattern.
export const isSentinelSection = (section) =>
  /\{\{subsection_container_[^}]+\}\}/.test(section?.type ?? '') ||
  /\{\{subsection_container_[^}]+\}\}/.test(section?.name ?? '') ||
  isSentinelTitle(section?.name ?? '') ||
  /^section_template_/.test(section?.type ?? '');

const uuid = () => crypto.randomUUID();

const lexical = () => ({
  id: uuid(),
  type: "LexicalTextarea",
  data: { value: "", html: "", showPart: "all", lexicalId: uuid() },
});

const lexicalFilled = (value) => ({
  id: uuid(),
  type: "LexicalTextarea",
  data: {
    value,
    html: `<p style="text-align: justify;"><span style="white-space: pre-wrap;">${value}</span></p>`,
    showPart: "all",
    lexicalId: uuid(),
  },
});

const lexicalHint = (hint) => ({
  id: uuid(),
  type: "LexicalTextarea",
  data: { value: "", html: "", showPart: "all", lexicalId: uuid(), hint },
});

const lexicalHintFilled = (value, hint) => ({
  id: uuid(),
  type: "LexicalTextarea",
  data: {
    value,
    html: `<p style="text-align: justify;"><span style="white-space: pre-wrap;">${value}</span></p>`,
    showPart: "all",
    lexicalId: uuid(),
    hint,
  },
});

const sub = (title, blocks) => ({
  id: uuid(),
  type: "subsection2",
  name: "Uusi aliosio",
  data: { title, blocks },
});
const numberedSub = (title, blocks) => ({
  id: uuid(),
  type: "subsection",
  name: "Uusi aliosio",
  data: { title, blocks },
});
const kuntoRow = (label) => ({
  id: uuid(),
  kunto: "KL3",
  label,
  description: { lexicalId: uuid(), value: "", html: "" },
});

const vaihtoehdotSection = (costUnit = "") => ({
  id: uuid(),
  label: "",
  bullets: [{ id: uuid(), text: "" }],
  costLabel: "Karkea kustannusarvio",
  costMin: "",
  costMax: "",
  costUnit,
  totalCostMin: "",
  totalCostMax: "",
  lifecycle: "",
});

const JULKISIVU_ROWS = CATEGORY_KUNTO_ROWS.Julkisivut;
const PARVEKE_ROWS   = CATEGORY_KUNTO_ROWS.Parvekkeet;
const IKKUNA_ROWS    = CATEGORY_KUNTO_ROWS.Ikkunat;
const OVI_ROWS       = CATEGORY_KUNTO_ROWS.Ovet;
const RYOMINTATILA_ROWS =
  CATEGORY_KUNTO_ROWS["Ryömintätilat"];
  const VESIKATTO_ROWS = CATEGORY_KUNTO_ROWS["Vesikatot"];
function buildKuntoTable(title, rows) {
  return {
    id: uuid(),
    type: "KuntoTable",
    data: {
      headerLabel: title,
      rows: rows.map(kuntoRow),
    },
  };
}

function toGenitiveTitle(title) {
  const suffixMap = [
    [/ikkunat$/i, 'ikkunoiden'],
    [/ovet$/i,    'ovien'],
    [/parvekkeet$/i, 'parvekkeiden'],
  ];
  const words = title.split(' ');
  const last = words[words.length - 1];
  for (const [pattern, replacement] of suffixMap) {
    if (pattern.test(last)) {
      words[words.length - 1] = last.replace(pattern, replacement);
      return words.join(' ');
    }
  }
  return title;
}
function buildRyomintatilaVaihtoehdot() {
  return {
    id: uuid(),
    type: "VaihtoehdotBlock",
    data: {
      value: "",
      showPart: "all",
      options: [
        {
          id: uuid(),
          title: "Ryömintätilojen kunnostus",
          sections: [
            vaihtoehdotSection(""),
          ],
        },
      ],
    },
  };
}
function buildVaihtoehdot(title, isIkkuna, isOvi) {
  const costUnit = isIkkuna ? "ikkuna" : isOvi ? "ovi" : "";
  const genitiveTitle = toGenitiveTitle(title);
  const mainLabel = isOvi ? `${genitiveTitle} huolto- ja kunnossapito` : `${genitiveTitle} kunnostukset`;
  const repairLabel = isIkkuna ? "Ikkunoiden uusiminen" : isOvi ? "Ulko-ovien uusiminen" : "Uusiminen";

  return {
    id: uuid(),
    type: "VaihtoehdotBlock",
    data: {
      value: "",
      showPart: "all",
      options: [
        {
          id: uuid(),
          title: mainLabel,
          sections: [vaihtoehdotSection(costUnit)],
        },
        {
          id: uuid(),
          title: repairLabel,
          sections: [vaihtoehdotSection(costUnit)],
        },
      ],
    },
  };
}

const imageBlock = (category, subCategory = null, pdfLayout = "large") => ({
  id: uuid(),
  type: "ImageBlock",
  data: { value: "", showPart: "all", category, subCategory, pdfLayout, subCategoryIsLocked: subCategory != null },
});

const measurementBlock = (type, name) => ({
  id: uuid(),
  type,
  name,
  data: { value: "", showPart: "all" },
});

const kuntoarviolauseBlock = (subject) => ({
  id: uuid(),
  type: "KuntoarviolauseBlock",
  name: "Kuntoarviolause",
  data: { value: "", showPart: "all", subject, kl: "KL3" },
});

const KLORIDI_LEXICAL_STATE = {
  root: {
    children: [
      { children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "5f5fd055-442d-46e0-828c-85c077d5ddb3" },
      { children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "2f7af054-c571-43d9-b0b0-0466f0233591" },
      {
        children: [
          {
            children: [
              {
                children: [{ children: [{ detail: 0, format: 0, mode: "normal", style: "", text: "Tunnus", type: "text", version: 1, id: "05853203-65cd-46b8-b9ce-7bbc769f60fb" }], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "283c3b4d-f34a-4cdc-9af7-c24fc694afb8" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 1, rowSpan: 1, id: "c2c8449e-5840-4685-9f04-6252d105fda6",
              },
              {
                children: [{ children: [{ detail: 0, format: 0, mode: "normal", style: "", text: "Rakenneosa", type: "text", version: 1, id: "49be8b7d-df23-41d1-987e-651c0173f5f5" }], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "87364e0b-8a05-4045-8c3b-aa2972edebc7" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 1, rowSpan: 1, id: "ee346250-45ad-4259-81c4-755c0a714835",
              },
              {
                children: [{ children: [{ detail: 0, format: 0, mode: "normal", style: "", text: "Kloridipitoisuus betonin painosta (%)", type: "text", version: 1, id: "d8048378-4519-4374-89b9-76846b91d563" }], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "3e9e23bd-25f4-4283-b2c1-9dd57611c0ce" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 1, rowSpan: 1, id: "88880935-c54e-44bf-91aa-275d203cea92",
              },
            ],
            direction: null, format: "", indent: 0, type: "tablerow", version: 1, id: "f47bc11f-02aa-4c5a-b76e-ff37612ad821",
          },
          {
            children: [
              {
                children: [{ children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "39eb4756-f97b-4956-93c7-ea41e849e4e2" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 0, rowSpan: 1, id: "975a9406-362d-4272-89cd-96e3ae3615c5",
              },
              {
                children: [{ children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "db83775f-e81b-4dc4-a5b4-cc7ffa5109a3" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 0, rowSpan: 1, id: "98bdf54c-2c29-48be-85dd-5b023d3270ea",
              },
              {
                children: [{ children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "85211f26-175b-42c0-bf4f-ac3175c52fad" }],
                direction: null, format: "", indent: 0, type: "tablecell", version: 1, backgroundColor: null, colSpan: 1, headerState: 0, rowSpan: 1, id: "404befdc-013a-4bf6-9e69-3c589adac8bb",
              },
            ],
            direction: null, format: "", indent: 0, type: "tablerow", version: 1, id: "4ad6dd86-973e-4b5d-b8a9-10d6f3a4d66a",
          },
        ],
        direction: null, format: "", indent: 0, type: "table", version: 1, id: "4af6d291-6ceb-48c3-ad7d-8a2aaa007f51",
      },
      { children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0, textStyle: "", id: "e0b319a0-ccac-4d07-9b56-8a8e3fb840ba" },
    ],
    direction: null, format: "", indent: 0, type: "root", version: 1, id: "be2e6547-a869-406f-b647-199fdaa30203",
  },
};

const kloridAnalyysiBlock = () => ({
  id: uuid(),
  type: "LexicalTextarea",
  data: {
    value: "\n\n\n\nTunnus\n\nRakenneosa\n\nKloridipitoisuus betonin painosta (%)\n\n\n\n\n\n\n\n\n\n",
    html: '<p><br></p><p><br></p><table class="lx-table"><colgroup><col><col><col></colgroup><tbody><tr><th class="lx-table-cell lx-table-cell-header" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start; background-color: rgb(242, 243, 245);"><p><span style="white-space: pre-wrap;">Tunnus</span></p></th><th class="lx-table-cell lx-table-cell-header" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start; background-color: rgb(242, 243, 245);"><p><span style="white-space: pre-wrap;">Rakenneosa</span></p></th><th class="lx-table-cell lx-table-cell-header" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start; background-color: rgb(242, 243, 245);"><p><span style="white-space: pre-wrap;">Kloridipitoisuus betonin painosta (%)</span></p></th></tr><tr><td class="lx-table-cell" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start;"><p><br></p></td><td class="lx-table-cell" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start;"><p><br></p></td><td class="lx-table-cell" style="border: 1px solid black; width: 75px; vertical-align: top; text-align: start;"><p><br></p></td></tr></tbody></table><p><br></p>',
    showPart: "all",
    lexicalId: uuid(),
    hint: "ANALYYSI",
    lexicalState: KLORIDI_LEXICAL_STATE,
  },
});

const KORJAUS_INTRO = "Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 0-1 vuoden kuluessa.";
const KORJAUS_OUTRO = "Korjauksen laadun ja onnistumisen varmistaminen edellyttää asiantuntevaa suunnittelua ja valvontaa. Kustannukset tarkentuvat suunnittelun ja kilpailutuksen edetessä.";
const ASBESTI_JOHTOPÄÄTÖS = "Näytteissä ei havaittu asbestia.";
const ASBESTI_YLEISTÄ = "Kiinteistö on rakennettu ennen vuotta 1994, jolloin asbestia on voitu käyttää rakennusmateriaaleissa. Kohteessa on tällöin lakisääteinen tarve teettää asbestikartoitus ennen purkutöihin ryhtymistä.";
const KLORIDI_YLEISTÄ = "Kloridipitoisuuden kriittisenä arvona voidaan pitää 0,03…0,07 p-%:a (lähde: by 42). Mikäli kloridipitoisuus ylittää tämän raja-arvon, kasvaa teräskorroosioriski selvästi. Näytteissä todettiin kohonnutta kloridipitoisuutta, joten tältä osin betonin teräskorroosioriski on hieman korostunut.";

function buildBetonitutkimusBlocks() {
  return [
    sub("Betonipeitepaksuudet ja karbonatisoitumissyvyys", [
      lexicalHint("ANALYYSI"),
      measurementBlock("PeitepaksuusBlock", "Peitepaksuus"),
      lexicalHint("ANALYYSI"),
      measurementBlock("PoranayteBlock", "Poranäyte"),
      lexicalHint("JOHTOPÄÄTÖS"),
      lexicalHint("YLEISTÄ"),
    ]),
    sub("Vetolujuus testaus", [
      lexicalHint("ANALYYSI"),
      measurementBlock("VetolujuusBlock", "Vetolujuus"),
      lexicalHint("JOHTOPÄÄTÖS"),
      lexicalHint("YLEISTÄ"),
    ]),
    sub("Ohuthietutkimus", [
      lexicalHint("ANALYYSI"),
      measurementBlock("OhuthieBlock", "Ohuthie"),
      lexicalHint("JOHTOPÄÄTÖS"),
      lexicalHint("YLEISTÄ"),
    ]),
    sub("Kloridipitoisuus", [
      kloridAnalyysiBlock(),
      lexicalHint("JOHTOPÄÄTÖS"),
      lexicalHintFilled(KLORIDI_YLEISTÄ, "YLEISTÄ  {KLORIDI}"),
    ]),
    sub("Asbestianalyysi", [
      lexicalHint("ANALYYSI"),
      lexicalHintFilled(ASBESTI_JOHTOPÄÄTÖS, "JOHTOPÄÄTÖS"),
      lexicalHintFilled(ASBESTI_YLEISTÄ, "YLEISTÄ {ASBESTI}"),
    ]),
  ];
}

function buildJulkisivutSubsection(title, imageCategory) {
  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",
    data: {
      title,
      blocks: [
        sub("Yleistä", [lexical(), imageBlock(imageCategory, null, "large"), lexical()]),
        sub("Julkisivujen silmämääräinen arviointi", [lexical()]),
        sub("Kuvat julkisivuista", [imageBlock(imageCategory, null, "normal")]),
        ...buildBetonitutkimusBlocks(),
        sub("Yhteenveto julkisivuista", [
          buildKuntoTable("Julkisivut", JULKISIVU_ROWS),
          lexical(),
          kuntoarviolauseBlock("julkisivujen"),
        ]),
        sub("Julkisivujen korjaussuositukset", [
          lexicalFilled(KORJAUS_INTRO),
          buildVaihtoehdot(title, false, false),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
      ],
    },
  };
}

function buildParvekkeetSubsection(title, imageCategory) {
  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",
    data: {
      title,
      blocks: [
        sub("Yleistä", [lexical(), imageBlock(imageCategory, null, "large"), lexical()]),
        sub("Parvekkeiden silmämääräinen arviointi", [lexical()]),
        sub("Kuvat parvekkeista", [imageBlock(imageCategory, null, "normal")]),
        ...buildBetonitutkimusBlocks(),
        sub("Yhteenveto parvekkeista", [
          buildKuntoTable(title, PARVEKE_ROWS),
          lexical(),
          kuntoarviolauseBlock("parvekkeiden"),
        ]),
        sub("Parvekkeiden korjaussuositukset", [
          lexicalFilled(KORJAUS_INTRO),
          buildVaihtoehdot(title, false, false),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
      ],
    },
  };
}
function buildRyomintatilatSubsection(
  title,
  imageCategory
) {
  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",

    data: {
      title,

      blocks: [
        sub("Yleistä", [
          lexical(),
        ]),

        sub(
          "Osa-alueet ja kuntoluokitus",
          [
            buildKuntoTable(
              "Ryömintätilat",
              RYOMINTATILA_ROWS
            ),
          ]
        ),

        sub(
          "Ryömintätilojen kunto",
          [
            lexical(),

            kuntoarviolauseBlock(
              "ryömintätilojen"
            ),
          ]
        ),

        sub(
          "Kuvat",
          [
            imageBlock(
              imageCategory,
              null,
              "normal"
            ),
          ]
        ),

        sub(
          "Korjaussuositukset",
          [
            lexicalFilled(
              "Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 0-2 vuoden kuluessa."
            ),

            buildRyomintatilaVaihtoehdot(),

            lexicalFilled(
              KORJAUS_OUTRO
            ),
          ]
        ),
      ],
    },
  };
}
function buildParvekkeetTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(), type: "subsection", name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        sub("Yleistä", [lexical(), imageBlock(imageCategory, DYN, "large"), lexical()]),
        sub(`${DYN} silmämääräinen arviointi`, [lexical()]),
        sub(`Kuvat ${DYN}`, [imageBlock(imageCategory, DYN, "normal")]),
        ...buildBetonitutkimusBlocks(),
        sub(`Yhteenveto – ${DYN}`, [
          buildKuntoTable(DYN, PARVEKE_ROWS),
          lexical(),
          kuntoarviolauseBlock(DYN),
        ]),
        sub(`Korjaussuositukset – ${DYN}`, [
          lexicalFilled(KORJAUS_INTRO),
          buildVaihtoehdot(DYN, false, false),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
      ],
    },
  };
}

function buildJulkisivutTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(), type: "subsection", name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        sub("Yleistä", [lexical(), imageBlock(imageCategory, DYN, "large"), lexical()]),
        sub(`${DYN} silmämääräinen arviointi`, [lexical()]),
        sub(`Kuvat ${DYN}`, [imageBlock(imageCategory, DYN, "normal")]),
        ...buildBetonitutkimusBlocks(),
        sub(`Yhteenveto – ${DYN}`, [
          buildKuntoTable(DYN, JULKISIVU_ROWS),
          lexical(),
          kuntoarviolauseBlock(DYN),
        ]),
        sub(`Korjaussuositukset – ${DYN}`, [
          lexicalFilled(KORJAUS_INTRO),
          buildVaihtoehdot(DYN, false, false),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
      ],
    },
  };
}
function buildVesikatotTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        numberedSub("Yleistä", [
          lexical(),
        ]),

        numberedSub("Osa-alueet ja kuntoluokitus", [
          buildKuntoTable(
            DYN,
            VESIKATTO_ROWS
          ),
        ]),

       numberedSub("Vesikatteiden kunto", [
  sub("Vesikatteet", [
    lexical(),
  ]),

  sub("Läpiviennit", [
    lexical(),
  ]),

  sub("Pellitykset", [
    lexical(),
  ]),

  sub("Kattoturvatuotteet", [
    lexical(),
  ]),

  sub("Sadevesijärjestelmä", [
    lexical(),
  ]),

  sub("Aluskatteet", [
    lexical(),
  ]),

  sub("Yläpohjat", [
    lexical(),
  ]),
          kuntoarviolauseBlock("vesikatteiden"),
        ]),

        numberedSub("Kuvat", [
          {
            id: uuid(),
            type: "ImageBlock",
            data: {
              category: imageCategory,
              subCategory: DYN,
              subCategoryIsLocked: true,
            },
          },
        ]),

numberedSub("Korjaussuositukset", [
  lexicalFilled(KORJAUS_INTRO),
  buildVaihtoehdot(DYN, false, false),
  lexicalFilled(KORJAUS_OUTRO),
]),
      ],
    },
  };
}
function buildIkkunatTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(), type: "subsection", name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        sub("Yleistä", [lexical()]),
        sub("Osien tyypit ja kuntoluokitus", [buildKuntoTable(DYN, IKKUNA_ROWS)]),
        sub(`${DYN} kunto`, [lexical(), kuntoarviolauseBlock(DYN)]),
        sub("Korjaussuositukset", [
          lexicalFilled("Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 1-2 vuoden kuluessa."),
          buildVaihtoehdot(DYN, true, false),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
        sub("Kuvat", [{ id: uuid(), type: "ImageBlock", data: { category: imageCategory, subCategory: DYN, subCategoryIsLocked: true } }]),
      ],
    },
  };
}
function buildRyomintatilatTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        numberedSub("Yleistä", [
          lexical(),
        ]),

        numberedSub("Osa-alueet ja kuntoluokitus", [
          buildKuntoTable(
            DYN,
            RYOMINTATILA_ROWS
          ),
        ]),

        numberedSub("Ryömintätilojen kunto", [
          lexical(),
          kuntoarviolauseBlock("ryömintätilojen"),
        ]),

        numberedSub("Kuvat", [
          {
            id: uuid(),
            type: "ImageBlock",
            data: {
              category: imageCategory,
              subCategory: DYN,
              subCategoryIsLocked: true,
            },
          },
        ]),

        numberedSub("Korjaussuositukset", [
          lexicalFilled(
            "Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 0-2 vuoden kuluessa."
          ),

          buildRyomintatilaVaihtoehdot(),

          lexicalFilled(KORJAUS_OUTRO),
        ]),
      ],
    },
  };
}
function buildOvetTemplateSentinelBlock(imageCategory) {
  return {
    id: uuid(), type: "subsection", name: "Uusi aliosio",
    data: {
      title: TEMPLATE_SENTINEL,
      blocks: [
        sub("Yleistä", [lexical()]),
        sub("Osien tyypit ja kuntoluokitus", [buildKuntoTable(DYN, OVI_ROWS)]),
        sub(`${DYN} kunto`, [lexical(), kuntoarviolauseBlock(DYN)]),
        sub("Korjaussuositukset", [
          lexicalFilled("Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 1-2 vuoden kuluessa."),
          buildVaihtoehdot(DYN, false, true),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
        sub("Kuvat", [{ id: uuid(), type: "ImageBlock", data: { category: imageCategory, subCategory: DYN, subCategoryIsLocked: true } }]),
      ],
    },
  };
}

export function buildTemplateSentinelBlock(category, imageCategory = "Yleinen") {
  switch (category) {
    case 'Julkisivut': return buildJulkisivutTemplateSentinelBlock(imageCategory);
    case 'Parvekkeet': return buildParvekkeetTemplateSentinelBlock(imageCategory);
    case 'Ikkunat': return buildIkkunatTemplateSentinelBlock(imageCategory);
       case "Ryömintätilat":
      return buildRyomintatilatTemplateSentinelBlock(
        imageCategory
      );
      case "Vesikatot":
    return buildVesikatotTemplateSentinelBlock(imageCategory);
    case 'Ovet': return buildOvetTemplateSentinelBlock(imageCategory);
    default: return {
      id: uuid(), type: "subsection", name: "Uusi aliosio",
      data: {
        title: TEMPLATE_SENTINEL,
        blocks: [sub("Yleistä", [lexical()])],
      },
    };
  }
}

export function buildSubsectionBlocks(title, category = null) {
  const template =
    SUBSECTION_TEMPLATES.find(t =>
      t.suggestions.some(s => s.toLowerCase() === title.toLowerCase())
    ) ?? (category ? SUBSECTION_TEMPLATES.find(t => t.category === category) : null);

  const imageCategory = template?.imageCategory ?? "Yleinen";
  const effectiveCategory = template?.category ?? category;

  const isIkkuna = effectiveCategory === "Ikkunat" || /ikkuna/i.test(title);
  const isOvi = effectiveCategory === "Ovet" || /\bovi\b|parvekeovi|parvekkeen/i.test(title);
  const isJulkisivu = effectiveCategory === "Julkisivut" || /julkisivu/i.test(title);
  const isParveke = effectiveCategory === "Parvekkeet" || (/parveke/i.test(title) && !isOvi);
  const isRyomintatila =
  effectiveCategory === "Ryömintätilat" ||
  /ryömintätila|alapohja/i.test(title);

  if (isJulkisivu) return buildJulkisivutSubsection(title, imageCategory);
  if (isParveke) return buildParvekkeetSubsection(title, imageCategory);
  if (isRyomintatila) return buildRyomintatilatSubsection(title, imageCategory);
  const isVesikatto = effectiveCategory === "Vesikatot" || /vesikatto/i.test(title);

  const konditionTitle = isIkkuna
    ? "Ikkunoiden kunto"
    : isOvi
    ? "Ovien kunto"
    : isJulkisivu
    ? "Julkisivun kunto"
    : isVesikatto
    ? "Vesikatteiden kunto"
    : "Kunto";

  const tableRows = isIkkuna ? IKKUNA_ROWS : isOvi ? OVI_ROWS : [];

  return {
    id: uuid(),
    type: "subsection",
    name: "Uusi aliosio",
    data: {
      title,
      blocks: [
        sub("Yleistä", [lexical()]),
        sub("Osien tyypit ja kuntoluokitus", [
          buildKuntoTable(title, tableRows),
        ]),
        sub(konditionTitle, [
          lexical(),
          {
            id: uuid(),
            type: "KuntoarviolauseBlock",
            data: {
              subject: isIkkuna ? "ikkunoiden" : isOvi ? "ovien" : "",
              kl: "KL3",
            },
          },
        ]),
        sub("Korjaussuositukset", [
          lexicalFilled("Suositeltu korjaustapa on tässä esitetty vain pääpiirteisesti ja kustannusarvio on siten vain suuntaa antava. Kohteen korjaussuositukset suositellaan toteuttamaan 1-2 vuoden kuluessa."),
          buildVaihtoehdot(title, isIkkuna, isOvi),
          lexicalFilled(KORJAUS_OUTRO),
        ]),
        sub("Kuvat", [
          {
            id: uuid(),
            type: "ImageBlock",
            data: {
              category: imageCategory,
              subCategory: title,
              subCategoryIsLocked: true,
            },
          },
        ]),
      ],
    },
  };
}
